/**
 * Cleeng final-summary display price priority — extracted from
 * components/layout-workout-landing/final-summary/index.js so the
 * fallback chain is unit testable.
 *
 * Dynamic Cleeng checkout has two different amounts:
 * - cycle-1 summary uses local excl-tax fields for app display
 * - next-recurring terms use local excl-tax fields
 */
import {
  formatCleengAmount,
  formatCleengMonthlyPrice as formatCleengMonthlyPriceValue,
  getCleengCurrentPriceText,
  normalizeCleengAmount,
  shouldUseCleengOfferTotalAsCurrent,
} from "./cleengPricing";

export { alignTermsAmountToSummaryPrice } from "./checkoutRecurringTerms";

export function selectCleengSummaryDisplayPrice(cleengPrice, {
  isDynamicCleengPricing,
  isStyledCleengSummary,
  pageSource,
}) {
  if (!cleengPrice) {
    return null;
  }

  const redeemSelectedOfferPriceText = selectCleengRedeemSelectedOfferPriceText(cleengPrice, {
    isDynamicCleengPricing,
    pageSource,
  });
  if (redeemSelectedOfferPriceText) {
    return redeemSelectedOfferPriceText;
  }

  if (isStyledCleengSummary) {
    if (isDynamicCleengPricing) {
      return (
        cleengPrice.discounted_price_excl_tax_text
        || cleengPrice.main_price_excl_tax_text
        || cleengPrice.total_price_excl_tax_text
        || cleengPrice.main_price_text
        || cleengPrice.total_price_text
        || cleengPrice.discounted_price_text
        || null
      );
    }
    return (
      getCleengCurrentPriceText(cleengPrice)
      || cleengPrice.discounted_price_text
      || cleengPrice.main_price_text
      || cleengPrice.total_price_text
      || null
    );
  }

  return (
    cleengPrice.main_price_text
    || cleengPrice.discounted_price_text
    || cleengPrice.total_price_text
    || null
  );
}

export function getCleengCurrencyMarker(text) {
  if (!text) {
    return null;
  }

  const normalizedText = String(text).trim();
  const currencyMatchers = [
    { regex: /(\bIDR\b|Rp(?=\s*\d))/i, code: "IDR" },
    { regex: /(\bSGD\b|S\$)/i, code: "SGD" },
    { regex: /(\bAUD\b|A\$)/i, code: "AUD" },
    { regex: /(\bCAD\b|C\$)/i, code: "CAD" },
    { regex: /(\bHKD\b|HK\$)/i, code: "HKD" },
    { regex: /(\bNZD\b|NZ\$)/i, code: "NZD" },
    { regex: /(\bUSD\b|US\$|\$(?=\s*\d))/i, code: "USD" },
    { regex: /(\bTHB\b|\u0e3f)/i, code: "THB" },
    { regex: /(\bJPY\b|\u00a5)/i, code: "JPY" },
    { regex: /(\bKRW\b|\u20a9)/i, code: "KRW" },
    { regex: /(\bMYR\b|RM(?=\s*\d))/i, code: "MYR" },
    { regex: /(\bPHP\b|\u20b1)/i, code: "PHP" },
    { regex: /(\bTWD\b|NT\$)/i, code: "TWD" },
    { regex: /(\bVND\b|\u20ab)/i, code: "VND" },
    { regex: /(\bGBP\b|\u00a3)/i, code: "GBP" },
    { regex: /(\bEUR\b|\u20ac)/i, code: "EUR" },
  ];

  const matchedCurrency = currencyMatchers.find(({ regex }) => regex.test(normalizedText));
  return matchedCurrency ? matchedCurrency.code : null;
}

export function selectCleengRecurringDisplayPrice(cleengPrice, {
  isDynamicCleengPricing,
  expectedCurrency,
} = {}) {
  if (!cleengPrice) {
    return null;
  }

  // For dynamic Cleeng (foreign customer with local currency), the "Next you
  // will be charged" line must show the **next-cycle** price in the customer's
  // local currency. For BOGO/promo offerings cycle 1 is discounted (main_price)
  // while cycle 2+ is the full retail (total_price), so prefer total_price.
  const dynamicCandidates = [
    cleengPrice.next_cycle_price_text,
    cleengPrice.next_cycle_price_excl_tax_text,
    cleengPrice.recurring_price_text,
    cleengPrice.recurring_price_excl_tax_text,
    cleengPrice.total_price_excl_tax_text,
    cleengPrice.total_price_text,
    cleengPrice.main_price_excl_tax_text,
    cleengPrice.main_price_text,
    cleengPrice.discounted_price_excl_tax_text,
  ];

  const staticCandidates = [
    cleengPrice.total_price_text,
    cleengPrice.main_price_text,
    cleengPrice.discounted_price_text,
  ];

  const candidates = isDynamicCleengPricing ? dynamicCandidates : staticCandidates;

  if (expectedCurrency) {
    const normalizedExpected = String(expectedCurrency).toUpperCase();
    let firstMatchingCurrency;
    let firstMatchingPositiveCurrency;

    candidates.forEach((text) => {
      if (!text) {
        return;
      }
      const marker = getCleengCurrencyMarker(text);
      if (!marker || marker.toUpperCase() !== normalizedExpected) {
        return;
      }

      if (!firstMatchingCurrency) {
        firstMatchingCurrency = text;
      }
      const parsedAmount = parseCleengAmount(text);
      if (Number.isFinite(parsedAmount) && parsedAmount > 0 && !firstMatchingPositiveCurrency) {
        firstMatchingPositiveCurrency = text;
      }
    });
    if (firstMatchingPositiveCurrency) {
      return firstMatchingPositiveCurrency;
    }
    if (firstMatchingCurrency) {
      return firstMatchingCurrency;
    }
  }

  return candidates.find(Boolean) || null;
}

export function selectCleengRedeemBogoCycleOnePriceText(cleengPrice, fallbackTexts = []) {
  if (!cleengPrice) {
    return null;
  }

  const candidateFields = [
    cleengPrice.discounted_price_excl_tax_text,
    cleengPrice.discounted_price_text,
    cleengPrice.discounted_price_excl_tax,
    cleengPrice.discounted_price,
    cleengPrice.main_price_excl_tax_text,
    cleengPrice.main_price_text,
    cleengPrice.main_price_excl_tax,
    cleengPrice.main_price,
    ...fallbackTexts,
  ];

  const freeCycleValue = candidateFields.find((value) => {
    const parsed = parseCleengAmount(value);
    return Number.isFinite(parsed) && parsed <= 0;
  });

  if (freeCycleValue === undefined || freeCycleValue === null) {
    return null;
  }

  return formatCleengAmount(cleengPrice, 0) || String(freeCycleValue);
}

const selectPositiveAmount = (values = []) => {
  for (let index = 0; index < values.length; index += 1) {
    const parsedValue = parseCleengAmount(values[index]);
    if (Number.isFinite(parsedValue) && parsedValue > 0) {
      return parsedValue;
    }
  }

  return null;
};

export function selectCleengRedeemSelectedOfferPriceText(cleengPrice, {
  isDynamicCleengPricing,
  pageSource,
} = {}) {
  if (!cleengPrice) {
    return null;
  }

  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");
  if (!["redeem", "redeem_buy_1_get_1"].includes(normalizedPageSource)) {
    return null;
  }

  const offerAmount = selectPositiveAmount([
    cleengPrice.offer_price,
    cleengPrice.offer_price_text,
  ]);
  if (!Number.isFinite(offerAmount)) {
    return null;
  }

  const expectedCurrency = String(cleengPrice.currency || "").toUpperCase();
  const totalTextCandidates = isDynamicCleengPricing
    ? [
      cleengPrice.total_price_excl_tax_text,
      cleengPrice.total_price_text,
      cleengPrice.offer_price_text,
    ]
    : [
      cleengPrice.total_price_text,
      cleengPrice.total_price_excl_tax_text,
      cleengPrice.offer_price_text,
    ];
  const selectedTotalText = totalTextCandidates.find((text) => {
    if (!text) {
      return false;
    }
    const marker = getCleengCurrencyMarker(text);
    if (expectedCurrency && marker && marker !== expectedCurrency) {
      return false;
    }
    const amount = parseCleengAmount(text);
    return Number.isFinite(amount) && amount > 0;
  });
  const selectedTotalAmount = selectPositiveAmount([
    selectedTotalText,
    cleengPrice.total_price_excl_tax,
    cleengPrice.total_price,
  ]);
  if (!Number.isFinite(selectedTotalAmount)) {
    return null;
  }

  const staleConfiguredAmount = selectPositiveAmount([
    cleengPrice.discounted_price_excl_tax,
    cleengPrice.discounted_price,
    cleengPrice.main_price_excl_tax,
    cleengPrice.main_price,
    cleengPrice.discounted_price_excl_tax_text,
    cleengPrice.discounted_price_text,
    cleengPrice.main_price_excl_tax_text,
    cleengPrice.main_price_text,
  ]);
  const retailAmount = selectPositiveAmount([
    cleengPrice.retail_price_excl_tax,
    cleengPrice.retail_price,
    cleengPrice.retail_price_converted,
    cleengPrice.retail_price_converted_excl_tax,
    cleengPrice.retail_price_excl_tax_text,
    cleengPrice.retail_price_text,
    cleengPrice.retail_price_converted_text,
  ]);

  const isUsdOffer = String(cleengPrice.offer_currency || "").toUpperCase() === "USD";
  const usesOfferTotal = isDynamicCleengPricing
    ? (
      isUsdOffer
      && (
        (Number.isFinite(staleConfiguredAmount) && staleConfiguredAmount > selectedTotalAmount + 0.01)
        || (Number.isFinite(retailAmount) && retailAmount > selectedTotalAmount + 0.01)
      )
    )
    : shouldUseCleengOfferTotalAsCurrent(cleengPrice);

  if (!usesOfferTotal) {
    return null;
  }

  return selectedTotalText || formatCleengAmount(cleengPrice, selectedTotalAmount);
}

export function selectCleengRedeemBogoRecurringPriceText(cleengPrice, {
  isDynamicCleengPricing,
  expectedCurrency,
  validityPeriodAmount,
} = {}) {
  if (!cleengPrice) {
    return null;
  }

  const selectedOfferPriceText = selectCleengRedeemSelectedOfferPriceText(cleengPrice, {
    isDynamicCleengPricing,
    pageSource: "redeem_buy_1_get_1",
  });
  if (selectedOfferPriceText) {
    return selectedOfferPriceText;
  }

  const normalizedExpectedCurrency = expectedCurrency
    ? String(expectedCurrency).toUpperCase()
    : null;
  const explicitRecurringCandidates = [
    cleengPrice.next_cycle_price_excl_tax_text,
    cleengPrice.next_cycle_price_text,
    cleengPrice.recurring_price_excl_tax_text,
    cleengPrice.recurring_price_text,
  ];
  const matchingExplicitRecurring = explicitRecurringCandidates.find((text) => {
    if (!text) {
      return false;
    }
    const marker = getCleengCurrencyMarker(text);
    if (normalizedExpectedCurrency && marker && marker !== normalizedExpectedCurrency) {
      return false;
    }
    const amount = parseCleengAmount(text);
    return Number.isFinite(amount) && amount > 0;
  });

  if (matchingExplicitRecurring) {
    return matchingExplicitRecurring;
  }

  const months = Number(validityPeriodAmount) > 0 ? Number(validityPeriodAmount) : 1;
  const totalCandidates = [
    cleengPrice.retail_price_excl_tax_text,
    cleengPrice.retail_price_text,
    cleengPrice.total_price_excl_tax_text,
    cleengPrice.total_price_text,
    cleengPrice.total_price_excl_tax,
    cleengPrice.total_price,
  ];
  const localRecurringTotalText = totalCandidates.find((value) => {
    const amount = parseCleengAmount(value);
    return typeof value === "string" && Number.isFinite(amount) && amount > 0;
  });

  if (months === 1 && localRecurringTotalText) {
    return localRecurringTotalText;
  }

  const localRecurringTotal = selectPositiveAmount(totalCandidates);
  if (localRecurringTotal) {
    return formatCleengAmount(cleengPrice, localRecurringTotal / months);
  }

  return selectCleengRecurringDisplayPrice(cleengPrice, {
    isDynamicCleengPricing,
    expectedCurrency,
  });
}

export function resolveCleengRedeemCheckoutVoucherDisplay({
  cleengPrice,
  isDynamicCleengPricing,
  pageSource,
  voucher,
  finalPriceText,
  packagePriceText,
  retailPriceText,
} = {}) {
  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");
  const isRedeemBogo = normalizedPageSource === "redeem_buy_1_get_1";

  // R4 GATE-3: live SG BOGO often has content.cleeng_price=null → isDynamic=false,
  // but checkout still paints packagePriceText from URL display_total (e.g. S$26.4).
  // Early-return used backend voucher.discount_text (USD -$59.85) and never reached
  // package-first candidates. Pin green discount to the visible package amount.
  // Totals/finalPriceText stay caller-owned on this non-dynamic branch.
  if (voucher && isRedeemBogo && !isDynamicCleengPricing) {
    const packageAmount = parseCleengAmount(packagePriceText);
    if (packagePriceText && Number.isFinite(packageAmount) && packageAmount > 0) {
      const discountText = `-${packagePriceText}`;
      return {
        voucher: {
          ...voucher,
          discount_text: discountText,
        },
        discountText,
        finalPriceText,
      };
    }
  }

  if (
    !voucher
    || !isRedeemBogo
    || !cleengPrice
    || !isDynamicCleengPricing
  ) {
    return {
      voucher: voucher || {},
      discountText: voucher && voucher.discount_text,
      finalPriceText,
    };
  }

  const expectedCurrency = String(cleengPrice.currency || "").toUpperCase();
  // R4: green discount must equal the *visible* package line above it.
  // Prefer packagePriceText (basePackagePriceText from checkout) before any
  // retail* candidate — retail-first produced S$97 package vs -S$150 discount
  // when FX/display retail drifted from display_total overlay.
  const packagePriceCandidates = [
    packagePriceText,
    retailPriceText,
    cleengPrice.retail_price_converted_text,
    cleengPrice.retail_price_excl_tax_text,
    cleengPrice.retail_price_text,
    cleengPrice.total_price_excl_tax_text,
    cleengPrice.total_price_text,
    cleengPrice.main_price_excl_tax_text,
    cleengPrice.main_price_text,
  ];
  const localPackagePriceText = packagePriceCandidates.find((text) => {
    if (!text) {
      return false;
    }
    const marker = getCleengCurrencyMarker(text);
    const amount = parseCleengAmount(text);
    return (!expectedCurrency || !marker || marker === expectedCurrency)
      && Number.isFinite(amount)
      && amount > 0;
  });
  const discountText = localPackagePriceText
    ? `-${localPackagePriceText}`
    : voucher.discount_text;
  const localizedFinalPriceText = formatCleengAmount(cleengPrice, 0) || finalPriceText;

  return {
    voucher: {
      ...voucher,
      discount_text: discountText,
      final_price_text: localizedFinalPriceText,
    },
    discountText,
    finalPriceText: localizedFinalPriceText,
  };
}

export function selectCleengUsdEquivalentPriceText(cleengPrice) {
  if (!cleengPrice) {
    return null;
  }

  if (
    String(cleengPrice.offer_currency || "").toUpperCase() === "USD"
    && Number.isFinite(Number(cleengPrice.offer_price))
  ) {
    return `$${Number(cleengPrice.offer_price).toFixed(2)}`;
  }

  const preferredFields = [
    "usd_equivalent_price_text",
    "recurring_usd_price_text",
    "next_cycle_usd_price_text",
    "offer_price_text",
    "main_price_usd_text",
    "total_price_usd_text",
    "price_text",
    "retail_price_text",
  ];

  const preferredValue = preferredFields
    .map((fieldName) => cleengPrice[fieldName])
    .find((value) => getCleengCurrencyMarker(value) === "USD");

  if (preferredValue) {
    return preferredValue;
  }

  return Object.keys(cleengPrice)
    .map((fieldName) => cleengPrice[fieldName])
    .find((value) => typeof value === "string" && getCleengCurrencyMarker(value) === "USD") || null;
}

const formatUsdAmount = (amount) => {
  if (!Number.isFinite(amount)) {
    return null;
  }

  return `$${amount.toFixed(2)}`;
};

const selectCleengRedeemBogoRecurringUsdEquivalentText = (cleengPrice, {
  validityPeriodAmount,
} = {}) => {
  if (!cleengPrice) {
    return null;
  }

  const explicitRecurringText = [
    cleengPrice.recurring_usd_price_text,
    cleengPrice.next_cycle_usd_price_text,
    cleengPrice.recurring_price_usd_text,
    cleengPrice.next_cycle_price_usd_text,
  ].find((text) => {
    const amount = parseCleengAmount(text);
    return getCleengCurrencyMarker(text) === "USD"
      && Number.isFinite(amount)
      && amount > 0;
  });

  if (explicitRecurringText) {
    return explicitRecurringText;
  }

  const usdEquivalentText = selectCleengUsdEquivalentPriceText(cleengPrice);
  const selectedOfferPriceText = selectCleengRedeemSelectedOfferPriceText(cleengPrice, {
    isDynamicCleengPricing: String(cleengPrice.currency || "").toUpperCase() !== "USD",
    pageSource: "redeem_buy_1_get_1",
  });
  if (selectedOfferPriceText) {
    return usdEquivalentText;
  }

  const usdEquivalentAmount = parseCleengAmount(usdEquivalentText);
  const months = Number(validityPeriodAmount) > 0 ? Number(validityPeriodAmount) : 1;

  if (Number.isFinite(usdEquivalentAmount) && usdEquivalentAmount > 0 && months > 1) {
    return formatUsdAmount(usdEquivalentAmount / months);
  }

  return usdEquivalentText;
};

export function resolveCleengBillingPeriod({
  pageSource,
  validityPeriodAmount,
  packageName,
} = {}) {
  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");
  const shouldForceMonth = ["premium", "promo", "promo/subs3", "plan", "redeem_buy_1_get_1"].includes(normalizedPageSource);
  const numericValidity = Number(validityPeriodAmount);

  if (shouldForceMonth && Number.isFinite(numericValidity) && numericValidity > 0) {
    return { amount: numericValidity, unit: "month" };
  }

  const match = String(packageName || "").match(/(\d+)\s+(day|week|month|year)/i);
  if (match) {
    const amount = Number(match[1]);
    const unit = match[2].toLowerCase();

    if (shouldForceMonth && unit === "day") {
      if (amount >= 28 && amount <= 31) {
        return { amount: 1, unit: "month" };
      }
      if (amount > 31 && amount % 30 === 0) {
        return { amount: amount / 30, unit: "month" };
      }
      return { amount: 1, unit: "month" };
    }

    if (shouldForceMonth) {
      return { amount: Number.isFinite(amount) && amount > 0 ? amount : 1, unit: "month" };
    }

    return { amount, unit };
  }

  if (shouldForceMonth) {
    return { amount: 1, unit: "month" };
  }

  return null;
}

const parseCleengAmount = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  return normalizeCleengAmount(value);
};

export function buildCleengRecurringTermsText({
  cleengPrice,
  isDynamicCleengPricing,
  pageSource,
  validityPeriodAmount,
  packageName,
  recurringPriceText,
  usdEquivalentPriceText,
  suppressUsdEquivalent = false,
  expectedCurrency,
} = {}) {
  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");
  const shouldSuppressUsdEquivalent = suppressUsdEquivalent;
  const period = resolveCleengBillingPeriod({
    pageSource,
    validityPeriodAmount,
    packageName,
  });
  const isRedeemBogoPage = normalizedPageSource === "redeem_buy_1_get_1";
  // R3 symbol boundary: only the recurring cadence branch below is in scope.
  // R4 voucher resolver + selectCleengRedeemSelectedOfferPriceText consumers
  // (summary display, amount short-circuit) stay unchanged for later workers.
  const effectiveUsdEquivalentText = shouldSuppressUsdEquivalent
    ? null
    : (usdEquivalentPriceText || (isRedeemBogoPage
      ? selectCleengRedeemBogoRecurringUsdEquivalentText(cleengPrice, { validityPeriodAmount })
      : selectCleengUsdEquivalentPriceText(cleengPrice)));
  const priceText = recurringPriceText
    || (isRedeemBogoPage
      ? selectCleengRedeemBogoRecurringPriceText(cleengPrice, {
        isDynamicCleengPricing,
        expectedCurrency,
        validityPeriodAmount,
      })
      : selectCleengRecurringDisplayPrice(cleengPrice, {
        isDynamicCleengPricing,
        expectedCurrency,
      }));

  if (!period || !priceText) {
    return null;
  }

  const amount = Number(period.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  const formatPeriodText = (rawPeriod) => {
    const periodAmount = Number(rawPeriod && rawPeriod.amount);
    if (!Number.isFinite(periodAmount) || periodAmount <= 0) {
      return null;
    }
    const periodUnit = periodAmount === 1 ? rawPeriod.unit : `${rawPeriod.unit}s`;
    return periodAmount === 1 ? periodUnit : `${periodAmount} ${periodUnit}`;
  };
  // Redeem Pay 1 Get 2: free-cycle length may be multi-month (`period`), but
  // post-entitlement renewal is always monthly. Do not reuse free-cycle period
  // when selected-offer price text is present (was: every N months bug).
  // Amount selection stays in selectCleengRedeemBogoRecurringPriceText /
  // selectCleengRedeemBogoRecurringUsdEquivalentText — do not divide selected
  // offer total by benefit months (aggregate GATE1 rejected alternative).
  const recurringPeriod = isRedeemBogoPage
    ? { amount: 1, unit: "month" }
    : period;
  const cadenceText = formatPeriodText(recurringPeriod);
  const freeCycleText = formatPeriodText(period) || cadenceText;
  const equivalentText = effectiveUsdEquivalentText;
  const chargedPriceText = isDynamicCleengPricing && equivalentText
    ? `~${priceText} (equivalent to ${equivalentText})`
    : priceText;

  if (!cadenceText) {
    return null;
  }

  const isRedeemFirstCycleFree = isRedeemBogoPage;

  if (isRedeemFirstCycleFree) {
    const freeVerb = Number(period.amount) === 1 ? "is" : "are";
    return `The first ${freeCycleText} ${freeVerb} free, then you will be charged ${chargedPriceText} every ${cadenceText} until canceled`;
  }

  return `Next you will be charged ${chargedPriceText} every ${cadenceText} until canceled`;
}

export function buildCleengCheckoutSubPriceDisplay({
  cleengPrice,
  pageSource,
  validityPeriodAmount,
  bogoEnabled,
  language,
  excludeTax = false,
} = {}) {
  const normalizedPageSource = String(pageSource || "").replace(/^\/+/, "");
  if (!cleengPrice || !["plan", "promo", "promo/subs3", "redeem_buy_1_get_1"].includes(normalizedPageSource)) {
    return null;
  }

  const months = Number(validityPeriodAmount) > 0 ? Number(validityPeriodAmount) : 1;
  const priceText = formatCleengMonthlyPriceValue(cleengPrice, {
    months,
    divideByBogo: bogoEnabled,
    excludeTax,
  });
  if (!priceText) {
    return null;
  }

  return {
    priceText,
    labelText: language === "id" ? "Per Orang / Bulan" : "Per Person / Month",
  };
}
